import { FormattedMessage } from 'react-intl';
import * as validationExpressions from 'myParkdUtils/validationExpressions';
import { MinimalUser, RegistrationCompanyData, RegistrationPaymentData, RegistrationUserData, RegistrationVehicleData } from 'types';
import type { JSX } from 'react';


export type ValidationError = JSX.Element | undefined | null;

export const validate = (errors: any) => {
    return Object.keys(errors).every(function (key) {
        return errors[key] === undefined;
    });
};

//====================================================================================
// VALIDATION CHECKS
//====================================================================================

export const checkIfNotNull = (inputValue: any): ValidationError => {
    if (!validationExpressions.isNotNull(inputValue)) {
        return <FormattedMessage id="VALIDATION_FIELD_IS_REQUIRED" />;
    }
};

// ----------------------------------------------------------------------------------

export const checkIfNotEmpty = (inputValue: any): ValidationError => {
    if (!validationExpressions.isNotEmpty(inputValue)) {
        return <FormattedMessage id="VALIDATION_FIELD_IS_REQUIRED" />;
    }
};

// ----------------------------------------------------------------------------------

export const checkIfNotZero = (inputValue: any): ValidationError => {
    if (!validationExpressions.isNotZero(inputValue)) {
        return <FormattedMessage id="VALIDATION_FIELD_IS_REQUIRED" />;
    }
};

// ----------------------------------------------------------------------------------

export const checkForMinimumLength = (inputValue: any, minLength: number): ValidationError => {
    if (!validationExpressions.hasMinimumLength(inputValue, minLength)) {
        return <FormattedMessage id="VALIDATION_MIN_CHARACTERS" values={{ minLength: minLength }} />;
    }
};

// ----------------------------------------------------------------------------------

export const checkForExactLength = (inputValue: any, exactLength: number): ValidationError => {
    if (!validationExpressions.hasExactLength(inputValue, exactLength)) {
        return <FormattedMessage id="VALIDATION_EXACT_CHARACTERS" values={{ length: exactLength }} />;
    }
};

// ----------------------------------------------------------------------------------

export const checkIfValidVAT = (inputValue: any): ValidationError => {
    if (!validationExpressions.isNotNull(inputValue)) {
        return <FormattedMessage id="VALIDATION_FIELD_IS_REQUIRED" />;
    } else if (!validationExpressions.startsWithCountryCode(inputValue)) {
        return <FormattedMessage id="VALIDATION_START_WITH_TWO_LETTERS" />;
    } else if (!validationExpressions.isValidVAT(inputValue)) {
        return <FormattedMessage id="VALIDATION_INVALID_VAT_NUMBER" />;
    }
};

// ----------------------------------------------------------------------------------

export const checkIfAccepted = (inputValue: any): ValidationError => {
    if (!validationExpressions.isAccepted(inputValue)) {
        return <FormattedMessage id="VALIDATION_CHECKBOX_NOT_ACCEPTED" />;
    }
};

// ----------------------------------------------------------------------------------

export const checkIfValidEmailAddress = (inputValue: string): ValidationError => {
    if (!validationExpressions.isNotNull(inputValue)) {
        return <FormattedMessage id="VALIDATION_FIELD_IS_REQUIRED" />;
    }
    if (!validationExpressions.isValidEmailAddress(inputValue)) {
        return <FormattedMessage id="VALIDATION_INVALID_EMAIL" />;
    }
};

// ----------------------------------------------------------------------------------

export const checkIfValidNonExistingEmailAddress = (inputValue: string, isExistingEmail: boolean): ValidationError => {
    if (!validationExpressions.isNotNull(inputValue)) {
        return <FormattedMessage id="VALIDATION_FIELD_IS_REQUIRED" />;
    }
    if (!validationExpressions.isValidEmailAddress(inputValue)) {
        return <FormattedMessage id="VALIDATION_INVALID_EMAIL" />;
    }
    if (isExistingEmail) {
        return <FormattedMessage id="PARKD_API_ERROR_EMAIL_ALREADY_EXISTS" />;
    }
};

// ----------------------------------------------------------------------------------

export const checkIfPhoneIsValid = (phoneNumber: string, isValidPhoneNumber: boolean): ValidationError => {
    if (phoneNumber === '') {
        return <FormattedMessage id="VALIDATION_FIELD_IS_REQUIRED" />;
    } else if (!validationExpressions.phoneNumberStatusIsValid(isValidPhoneNumber)) {
        return <FormattedMessage id="VALIDATION_INVALID_PHONENUMBER" />;
    }
};

// ----------------------------------------------------------------------------------

export const checkIfexpiryDateIsInFuture = (expiryDate: string): ValidationError => {
    if (!validationExpressions.isNotNull(expiryDate)) {
        return <FormattedMessage id="VALIDATION_FIELD_IS_REQUIRED" />;
    }
    if (!validationExpressions.expiryDateIsInFuture(expiryDate)) {
        return <FormattedMessage id="VALIDATION_EXPIRY_DATE_NOT_IN_FUTURE" />;
    }
};

// =====================================================================================

export const validateVehicleInput = (vehicleData: RegistrationVehicleData) => {
    return {
        brand: checkIfNotEmpty(vehicleData.brand) || null,
        model: checkIfNotEmpty(vehicleData.model) || null,
        licensePlate: checkIfNotEmpty(vehicleData.licensePlate) || null,
        vin: checkForExactLength(vehicleData.vin, 17) || null,
        mileage: checkIfNotEmpty(vehicleData.mileage) || null,
        month: checkIfNotZero(vehicleData.month) || null,
        year: checkIfNotZero(vehicleData.year) || null,
    };
};

export const validateUserInput = (userData: RegistrationUserData, isExistingEmail: boolean, isValidPhoneNumber: boolean) => {
    return {
        givenName: checkIfNotEmpty(userData.givenName) || null,
        familyName: checkIfNotEmpty(userData.familyName) || null,
        email: checkIfValidNonExistingEmailAddress(userData.email, isExistingEmail) || null,
        password: checkForMinimumLength(userData.password, 6) || null,
        checkboxAccepted: checkIfAccepted(userData.checkboxAccepted) || null,
        phone: checkIfPhoneIsValid(userData.phone, isValidPhoneNumber) || null,
    };
};

export const validateCompanyInput = (companyData: RegistrationCompanyData) => {
    return {
        address: {
            street: checkIfNotNull(companyData.address.street) || null,
            number: checkIfNotNull(companyData.address.number) || null,
            city: checkIfNotNull(companyData.address.city) || null,
            postalCode: checkIfNotNull(companyData.address.postalCode) || null,
        },
        name: checkIfNotNull(companyData.name) || null,
        vat: checkIfValidVAT(companyData.vat) || null,
        email: checkIfValidEmailAddress(companyData.email,) || null,
    };
};

export const validatePaymentInput = (paymentData: RegistrationPaymentData) => {
    return {
        cardNumber: checkIfNotNull(paymentData.data.cardNumber) || null,
        expiry: checkIfexpiryDateIsInFuture(paymentData.data.expiry) || null,
        cvc: checkForExactLength(paymentData.data.cvc, 3) || null,
    };
};

export const validateDriverInput = (user: MinimalUser, isValidPhoneNumber: boolean) => {
    // TODO: REPLACE 'isExistingEmail'
    const isExistingEmail = false;
    return {
        givenName: checkIfNotEmpty(user.givenName) || null,
        familyName: checkIfNotEmpty(user.familyName) || null,
        email: checkIfValidNonExistingEmailAddress(user.email, isExistingEmail) || null,
        phoneNumber: checkIfPhoneIsValid(user.phoneNumber, isValidPhoneNumber) || null,
    }
}
